import { Add, Delete } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import api from '../../api';
import CombinedPaymentsEditor from './CombinedPaymentsEditor';
import MoneyInput from '../MoneyInput';
import { formatCurrencyInCents } from '../../helpers/formatters';
import { createPaymentRow, syncSinglePaymentAmount } from '../../helpers/checkoutPayments';
import {
    channelForcesPaid,
    normalizeChannelKind,
    normalizeProductKind,
    productAllowedForChannel,
    productFilterHint,
} from '../../helpers/orderStages';

function BatchSaleEntry({
    index,
    sale,
    onChange,
    onRemove,
    canRemove,
    fieldErrors = {},
    defaultPaymentDate,
    channelKind = '',
}) {
    const [productKind, setProductKind] = useState('');
    const [productName, setProductName] = useState('');
    const [productUnitPrice, setProductUnitPrice] = useState('');
    const [productQuantity, setProductQuantity] = useState(1);
    const [productId, setProductId] = useState('');
    const [addProductErrors, setAddProductErrors] = useState({
        product_name: '',
        product_price: '',
        product_quantity: '',
    });
    const [isLoadingProductSearch, setIsLoadingProductSearch] = useState(false);
    const [productResults, setProductResults] = useState([]);
    const [anchorElProduct, setAnchorElProduct] = useState(null);
    const timerRef = useRef(null);

    const updateSale = (patch) => onChange({ ...sale, ...patch });

    const recomputeAmount = (products) => (
        products.reduce((sum, product) => sum + product.total, 0)
    );

    const productSearch = (name) => {
        if (name.length >= 3) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                setIsLoadingProductSearch(true);
                api.get(`products?q=${name}`)
                    .then((response) => {
                        const d = response.data;
                        const rows = Array.isArray(d?.products)
                            ? d.products
                            : Array.isArray(d?.data)
                                ? d.data
                                : [];
                        const kind = normalizeChannelKind(channelKind);
                        setProductResults(rows.filter((product) => productAllowedForChannel(kind, product.kind)));
                    })
                    .catch(() => setProductResults([]))
                    .finally(() => setIsLoadingProductSearch(false));
            }, 1000);
        } else {
            setProductResults([]);
        }
    };

    const handleAddProduct = () => {
        const unitPriceInCents = parseFloat(String(productUnitPrice).replace(/[^\d]/g, ''), 10);
        const currentQuantity = parseInt(productQuantity, 10);
        const currentProductErrors = {
            product_name: '',
            product_price: '',
            product_quantity: '',
        };
        let hasProductErrors = false;

        if (productName === '' && productId === '') {
            currentProductErrors.product_name = 'Informe o nome do produto';
            hasProductErrors = true;
        }
        if (Number.isNaN(unitPriceInCents) || unitPriceInCents <= 0) {
            currentProductErrors.product_price = 'Informe o valor do produto';
            hasProductErrors = true;
        }
        if (Number.isNaN(currentQuantity) || currentQuantity <= 0) {
            currentProductErrors.product_quantity = 'Informe a quantidade';
            hasProductErrors = true;
        }
        if (hasProductErrors) {
            setAddProductErrors(currentProductErrors);
            return;
        }

        setAddProductErrors({ product_name: '', product_price: '', product_quantity: '' });

        const product = {
            name: productName,
            base_price: unitPriceInCents,
            quantity: currentQuantity,
            total: unitPriceInCents * currentQuantity,
            kind: normalizeProductKind(productKind),
        };
        if (productId !== '') {
            product.id = productId;
        }
        setProductKind('');

        const products = [...sale.products, product];
        const amount = recomputeAmount(products);
        updateSale({
            products,
            amount,
            payments: sale.customerWillPayLater
                ? sale.payments
                : syncSinglePaymentAmount(sale.payments || [], amount),
        });

        setProductName('');
        setProductUnitPrice('');
        setProductQuantity(1);
        setProductId('');
    };

    const handleRemoveProduct = (productIndex) => {
        const products = sale.products.filter((_, i) => i !== productIndex);
        const amount = recomputeAmount(products);
        updateSale({
            products,
            amount,
            payments: sale.customerWillPayLater
                ? sale.payments
                : syncSinglePaymentAmount(sale.payments || [], amount),
        });
    };

    const handlePayLaterChange = (event) => {
        const checked = event.target.checked;
        updateSale({
            customerWillPayLater: checked,
            payments: checked
                ? []
                : [createPaymentRow({
                    amountCents: sale.amount || 0,
                    paymentDate: defaultPaymentDate || new Date(),
                })],
        });
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    Venda
                    {' '}
                    {index + 1}
                </Typography>
                {canRemove && (
                    <Button color="error" size="small" onClick={onRemove} startIcon={<Delete />}>
                        Remover
                    </Button>
                )}
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label={isLoadingProductSearch ? 'Buscando produto...' : 'Nome do produto'}
                        fullWidth
                        value={productName}
                        onChange={(e) => {
                            setProductName(e.target.value);
                            productSearch(e.target.value);
                        }}
                        onFocus={(e) => setAnchorElProduct(e.currentTarget)}
                    />
                    <Menu
                        anchorEl={anchorElProduct}
                        open={Boolean(anchorElProduct) && productResults.length > 0}
                        onClose={() => {
                            setAnchorElProduct(null);
                            setProductResults([]);
                        }}
                        disableAutoFocusItem
                    >
                        {productResults.map((product) => (
                            <MenuItem
                                key={product.uuid || product.id}
                                onClick={() => {
                                    setProductName(product.name);
                                    setProductUnitPrice(product.base_price / 100);
                                    setProductQuantity(1);
                                    setProductId(product.uuid || product.id || '');
                                    setProductKind(product.kind || '');
                                    setAnchorElProduct(null);
                                    setProductResults([]);
                                }}
                            >
                                {product.name} — {formatCurrencyInCents(product.base_price)}
                            </MenuItem>
                        ))}
                    </Menu>
                    {addProductErrors.product_name && (
                        <Typography variant="caption" color="error" display="block">
                            {addProductErrors.product_name}
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} sm={3}>
                    <MoneyInput
                        label="Valor unitário"
                        value={productUnitPrice}
                        onChange={setProductUnitPrice}
                    />
                    {addProductErrors.product_price && (
                        <Typography variant="caption" color="error" display="block">
                            {addProductErrors.product_price}
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} sm={2}>
                    <TextField
                        label="Qtd"
                        fullWidth
                        value={productQuantity}
                        onChange={(e) => setProductQuantity(e.target.value)}
                        inputMode="numeric"
                    />
                    {addProductErrors.product_quantity && (
                        <Typography variant="caption" color="error" display="block">
                            {addProductErrors.product_quantity}
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleAddProduct}
                        startIcon={<Add />}
                        fullWidth
                        sx={{ height: '56px' }}
                    >
                        Incluir produto
                    </Button>
                </Grid>

                {productFilterHint(channelKind) ? (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            {productFilterHint(channelKind)}
                        </Typography>
                    </Grid>
                ) : null}

                <Grid item xs={12}>
                    {sale.products.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Nenhum produto nesta venda.
                        </Typography>
                    ) : (
                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                            {sale.products.map((product, productIndex) => (
                                <li key={`${product.name}-${productIndex}`}>
                                    {product.name}
                                    {' '}
                                    -
                                    {' '}
                                    {product.quantity}
                                    {' '}
                                    x
                                    {' '}
                                    {formatCurrencyInCents(product.base_price)}
                                    {' '}
                                    =
                                    {' '}
                                    {formatCurrencyInCents(product.total)}
                                    <IconButton size="small" onClick={() => handleRemoveProduct(productIndex)}>
                                        <Delete color="error" fontSize="small" />
                                    </IconButton>
                                </li>
                            ))}
                        </ul>
                    )}
                    {fieldErrors.products && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {fieldErrors.products}
                        </Typography>
                    )}
                </Grid>

                {!channelForcesPaid(channelKind) ? (
                <Grid item xs={12}>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={sale.customerWillPayLater}
                                onChange={handlePayLaterChange}
                                color="primary"
                            />
                        )}
                        label="Pagamento em outro momento"
                    />
                </Grid>
                ) : (
                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                        Pagamento já realizado na plataforma. O repasse fica a liberar até a confirmação.
                    </Typography>
                </Grid>
                )}

                {!sale.customerWillPayLater && (
                    <Grid item xs={12}>
                        <CombinedPaymentsEditor
                            payments={sale.payments || []}
                            onChange={(payments) => updateSale({ payments })}
                            totalAmountCents={sale.amount || 0}
                            defaultDate={defaultPaymentDate}
                        />
                    </Grid>
                )}

                {fieldErrors.payment_methods && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="error">
                            {fieldErrors.payment_methods}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <TextField
                        label="Observações"
                        fullWidth
                        multiline
                        minRows={2}
                        value={sale.observations || ''}
                        onChange={(e) => updateSale({ observations: e.target.value })}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle1" align="right">
                        Total desta venda:
                        {' '}
                        <strong>{formatCurrencyInCents(sale.amount)}</strong>
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
}

export default BatchSaleEntry;
